<template>
  <div id="minting-view">
    <div class="minting-video-space">
      <div class="minting-video-aspect-setter">
        <div class="minting-video-container">
          <div class="minting-diagonal-cut"></div>
          <video
            loop="loop"
            autoplay=""
            playsinline=""
            muted=""
            preload="none"
            src="https://www.theqbits.art/wp-content/uploads/2022/02/video-web-final.mp4"
            class="minting-video-player"
          >
            <source type="video/mp4" src="https://www.theqbits.art/wp-content/uploads/2022/02/video-web-final.mp4">
          </video>
        </div>
      </div>
    </div>
    <div class="minting-card-holder">
      <v-card color="half-accent" min-width="50%" width="400" max-width="90%" class="mx-auto rounded-lg mt-11 py-4">
        <h1 class="text-uppercase font-weight-medium pb-8 pt-4">{{ title }}</h1>
        <div class="pa-12 text-center" v-if="isLoading">
          <v-progress-circular indeterminate class="mx-auto" size="60" width="5" />
        </div>
        <div class="double-accent py-8">
          <div class="d-flex justify-center align-center text-big">
            <v-btn icon large class="" :disabled="!minusEnabled" @click="decreaseTokens"><v-icon color="white" size="48">mdi-minus</v-icon></v-btn>
            <span class="token-count" >{{ tokensCount }}</span>
            <v-btn icon large class="" :disabled="!plusEnabled" @click="increaseTokens"><v-icon color="white" size="48">mdi-plus</v-icon></v-btn>
          </div>
          <div>
            <v-btn color="white" outlined @click="setMaxTokens">{{ $t('minting.setMaxTokens') }}</v-btn>
          </div>
        </div>
        <div class="px-2 pt-6 pb-2 text-body-2 font-weight-medium" v-if="!isWalletConnectedToChain || !saleIsActive">
          <div>{{ $t('minting.totalPrice') }}</div>
          <div>{{ totalPrice ? (totalPrice.toFixed(2) + ' ETH + gas') : '-' }}</div>
          <div class="text-body-4 font-italic pt-0" v-if="totalPriceUSD">~ USD {{ totalPriceUSD.toFixed(2) }} + gas</div>
        </div>
        <v-row class="px-2 pt-6 pb-2 pb-sm-6 text-body-2 font-weight-medium" v-else>
          <v-col cols="24" sm="12" class="col-border-right-sm pa-2">
            <div>{{ $t('minting.totalPrice') }}</div>
            <div>{{ totalPrice ? (totalPrice.toFixed(2) + ' ETH') : '-' }}</div>
          </v-col>
          <v-col cols="24" sm="12" class="col-border-left-sm pa-2">
            <div>{{ $t('minting.estimatedGas') }}</div>
            <div>{{ estimatedGas ? (estimatedGas.toFixed(4) + ' ETH') : '-' }}</div>
          </v-col>
          <v-col cols="24" sm="12" class="col-border-right-sm pa-2">
            <div>{{ $t('minting.accountBalance') }}</div>
            <div>{{ accountBalance ? (accountBalance.toFixed(2) + ' ETH') : '-' }}</div>
          </v-col>
          <v-col cols="24" sm="12" class="col-border-left-sm pa-2">
            <div>{{ $t('minting.accountTokensBalance') }}</div>
            <div>{{ accountTokensBalance ? (accountTokensBalance + ' QBITS') : '-' }} </div>
          </v-col>
        </v-row>
        <div class="pa-4">
          <v-btn v-if="!isWalletConnected"
            color="white"
            outlined
            x-large
            @click="$store.dispatch('web3Connect')"
            class="text-body-2"
          >{{ $t('shared.connectWallet') }}</v-btn>
          <v-btn v-else-if="!isWalletConnectedToChain"
            color="white"
            outlined
            x-large
            @click="$store.dispatch('web3SwitchChain')"
            class="text-body-2"
          >{{ $t('shared.switchToEth') }}</v-btn>
          <div v-else-if="!saleIsActive"
            class="pa-8 mx-8 text-body-2 font-weight-medium"
          >{{ $t('minting.saleStartsSoon') }}</div>
          <template v-else-if="isPresaleSelected">
            <v-btn
              color="white"
              outlined
              x-large
              @click="mintTokensPresale"
              :disabled="!presaleApprovalSignature"
              class="text-body-2"
            >{{ $t('minting.mintInPresale') }}</v-btn>
            <div v-if="!presaleApprovalSignature" class="pt-1 text-body-3">
              {{ $t('minting.notInPresale') }}
            </div>
          </template>
          <v-btn v-else-if="isSaleAvailable"
            color="white"
            outlined
            x-large
            @click="mintTokensSale"
            :disabled="maxTokensForAccount === 0"
            class="text-body-2"
          >{{ $t('minting.mintInSale') }}</v-btn>
        </div>
        <div v-if="totalTokensMinted !== null" class="mx-8">
          <div class="progress-holder">
            <v-progress-linear :value="totalTokensMinted / totalTokens * 100" height="8" rounded color="white" background-color="transparent" />
          </div>
          <div class="text-end px-2 pt-1 pb-1 text-body-3">{{ totalTokensMinted }} / {{ totalTokens }}</div>
        </div>
        <div v-if="isWalletConnectedToChain && !isPresaleSelected" class="mx-8">
          <v-btn small text :to="{ name: 'minting', query: { presale: null } }">
            {{ $t('minting.mintInPresale') }}
          </v-btn>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import { signedWhitelistApprovals } from '@/data/signed-whitelist-approvals.json'
import { maxTokensPerSale, tokensSalePrice, tokensPresalePrice, totalTokens, fetchEthPriceUri } from '@/data/constants.js'
import { ethers } from 'ethers'

export default {
  components: { },
  data () {
    return {
      isLoading: true,
      tokensCount: 1,
      publicSaleTimestamp: null,
      saleIsActive: null,
      totalTokensMinted: null,
      accountBalanceExact: null,
      accountTokensBalance: null,
      gasEstimation: {},
      ethPrice: null
    }
  },
  metaInfo () {
    return {
      title: 'The QBits - ' + this.title
    }
  },
  computed: {
    title () {
      return this.$t('minting.' + (this.isPresaleSelected ? 'titlePresale' : 'titleSale'))
    },
    web3LastChangeTimestamp () {
      return this.$store.getters.web3LastChangeTimestamp
    },
    ethProvider () {
      return this.$store.getters.ethProvider
    },
    ethContractInstance () {
      return this.$store.getters.ethContractInstance
    },
    isWalletConnected () {
      return this.$store.getters.web3Connected &&
          !!this.$store.getters.web3Account
    },
    isChainMatched () {
      return this.$store.getters.web3ChainMatch
    },
    isWalletConnectedToChain () {
      return this.isWalletConnected && this.isChainMatched
    },
    web3Account () {
      return this.$store.getters.web3Account
    },
    isWeb3Connected () {
      return !!this.ethProvider
    },
    isWeb3ConnectedToChain () {
      return this.isWeb3Connected && this.isChainMatched
    },
    isPresaleSelected () {
      return this.$route.query.presale !== undefined
    },
    isSaleAvailable () {
      return this.saleIsActive && (Date.now() / 1000) >= this.publicSaleTimestamp
    },
    maxTokensApprovedForPresale () {
      if (!this.web3Account) { return 0 }
      const approvalForAccount = signedWhitelistApprovals.whitelistApprovals[this.web3Account]
      return approvalForAccount ? approvalForAccount.approvedLimit : 0
    },
    presaleApprovalSignature () {
      if (!this.web3Account) { return null }
      const approvalForAccount = signedWhitelistApprovals.whitelistApprovals[this.web3Account]
      return approvalForAccount ? approvalForAccount.signature : null
    },
    maxTokensForAccount () {
      if (!this.isWalletConnectedToChain || !this.saleIsActive) {
        return maxTokensPerSale
      }
      const maxTokensPerMint = this.isPresaleSelected ? this.maxTokensApprovedForPresale : maxTokensPerSale
      const maxTokensLeftForAccount = this.accountTokensBalance !== null ? maxTokensPerSale - this.accountTokensBalance : maxTokensPerMint

      let maxTokensPayableByAccount = this.accountBalanceExact !== null ? Math.floor(this.accountBalance / tokensSalePrice) : maxTokensPerMint
      const estimatedGas = this.gasEstimation[maxTokensPayableByAccount]
      if (estimatedGas && this.accountBalanceExact) {
        const gasPlusPrice = ethers.utils.parseEther(tokensSalePrice.toFixed(2))
          .mul(maxTokensPayableByAccount)
          .add(estimatedGas)
        if (gasPlusPrice.gt(this.accountBalanceExact)) {
          maxTokensPayableByAccount--
        }
      }

      const maxTokensLeftInCollection = this.totalTokensMinted !== null ? totalTokens - this.totalTokensMinted : maxTokensPerMint

      return Math.max(
        Math.min(
          maxTokensPerMint,
          maxTokensLeftForAccount,
          maxTokensPayableByAccount,
          maxTokensLeftInCollection
        ), 0
      )
    },
    minusEnabled () {
      return this.tokensCount > 1
    },
    plusEnabled () {
      return this.tokensCount < this.maxTokensForAccount
    },
    totalPrice () {
      return this.isPresaleSelected
        ? this.tokensCount * tokensPresalePrice
        : this.tokensCount * tokensSalePrice
    },
    totalPriceUSD () {
      if (!this.totalPrice || !this.ethPrice) { return null }
      return this.totalPrice * this.ethPrice
    },
    totalTokens () {
      return totalTokens
    },
    accountBalance () {
      if (!this.accountBalanceExact) { return null }
      return Number(ethers.utils.formatEther(this.accountBalanceExact))
    },
    estimatedGas () {
      const gasEstimationForCount = this.gasEstimation[this.tokensCount]
      if (!gasEstimationForCount) { return null }
      return Number(ethers.utils.formatEther(gasEstimationForCount))
    }
  },
  methods: {
    decreaseTokens () {
      if (this.tokensCount > 1) {
        this.tokensCount--
      }
    },
    increaseTokens () {
      if (this.tokensCount < this.maxTokensForAccount) {
        this.tokensCount++
      }
    },
    setMaxTokens () {
      this.tokensCount = this.maxTokensForAccount
    },
    updateInfoFromChain () {
      this.isLoading = true
      const provisionInfoPromises = []
      if (this.isWalletConnectedToChain) {
        provisionInfoPromises.push(
          this.ethProvider.getBalance(this.web3Account)
            .then(result => { this.accountBalanceExact = result })
            .catch(() => { this.accountBalanceExact = null })
        )
        provisionInfoPromises.push(
          this.ethContractInstance.balanceOf(this.web3Account)
            .then(result => { this.accountTokensBalance = result.toNumber() })
            .catch(() => { this.accountTokensBalance = null })
        )
      } else {
        this.accountBalanceExact = null
        this.accountTokensBalance = null
      }
      if (this.isWeb3ConnectedToChain) {
        provisionInfoPromises.push(
          this.ethContractInstance.PUBLIC_SALE_TIMESTAMP()
            .then(result => { this.publicSaleTimestamp = result.toNumber() })
            .catch(() => { this.publicSaleTimestamp = null })
        )
        provisionInfoPromises.push(
          this.ethContractInstance.saleIsActive()
            .then(result => { this.saleIsActive = result })
            .catch(() => { this.saleIsActive = null })
        )
        provisionInfoPromises.push(
          this.ethContractInstance.totalSupply()
            .then(result => { this.totalTokensMinted = result.toNumber() })
            .catch(() => { this.totalTokensMinted = null })
        )
      } else {
        this.totalTokensMinted = null
      }
      Promise.all(provisionInfoPromises)
        .then(() => {
          if (this.tokensCount > this.maxTokensForAccount) {
            this.tokensCount = this.maxTokensForAccount
          }
          this.calculateGasFor(this.tokensCount)
          if (this.saleIsActive && !this.isSaleAvailable) {
            this.$router.replace({ name: 'minting', query: { presale: null } })
          }
        })
        .catch(error => {
          console.error(error) // eslint-disable-line no-console
          this.$store.dispatch('alertShow', { error: this.$t('error.fetchingContractData') })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    mintTokensSale () {
      if (this.isWalletConnectedToChain) {
        this.ethContractInstance.mintQBitsSale(this.tokensCount, {
          value: ethers.utils.parseEther(this.totalPrice.toFixed(2))
        })
          .then(mintQBitsSaleTx => mintQBitsSaleTx.wait())
          .then(() => {
            this.updateInfoFromChain()
          })
      }
    },
    mintTokensPresale () {
      if (this.isWalletConnectedToChain) {
        this.ethContractInstance.mintQBitsPresale(
          this.presaleApprovalSignature,
          this.tokensCount,
          this.maxTokensApprovedForPresale, {
            value: ethers.utils.parseEther(this.totalPrice.toFixed(2))
          }
        )
          .then(mintQBitsPresaleTx => mintQBitsPresaleTx.wait())
          .then(() => {
            this.updateInfoFromChain()
          })
      }
    },
    async calculateGasFor (tokensCount) {
      if (this.ethContractInstance && this.ethProvider) {
        const mintTxData = await this.ethContractInstance.populateTransaction.mintQBitsSale(tokensCount, {
          value: ethers.utils.parseEther(tokensSalePrice.toFixed(2))
            .mul(tokensCount)
        })
        const gasCost = await this.ethProvider.estimateGas(mintTxData)
        this.$set(this.gasEstimation, tokensCount, gasCost)
      }
    },
    async getEthPrice () {
      try {
        const priceFetchResponse = await fetch(fetchEthPriceUri, {
          method: 'GET',
          headers: {
            Accept: 'application/json'
          }
        })
        if (!priceFetchResponse.ok) { throw new Error() }
        const priceFetchResponseBody = await priceFetchResponse.json()
        this.ethPrice = priceFetchResponseBody.ethereum.usd
      } catch {}
    }
  },
  watch: {
    web3LastChangeTimestamp () {
      this.updateInfoFromChain()
    },
    maxTokensForAccount (newValue) {
      this.calculateGasFor(newValue)
      if (this.tokensCount > newValue) {
        this.tokensCount = newValue
      } else if (!this.tokensCount && newValue) {
        this.tokensCount = 1
      }
    },
    tokensCount (newValue) {
      this.calculateGasFor(newValue)
    }
  },
  mounted () {
    this.updateInfoFromChain()
    this.getEthPrice()
  }
}
</script>

<style lang="scss">
  @import '@/styles/main.scss';

  #minting-view {
    .minting-card-holder {
      z-index: 1;
      margin-bottom:400px;
      .token-count {
        text-align: center;
        min-width: 60px;
      }
    }
    .col-border-right-sm {
      @include smAndUp { border-right: 1px solid white; }
    }
    .col-border-left-sm {
      @include smAndUp { border-left: 1px solid white; }
    }
    .progress-holder {
      border: 2px solid white;
      padding: 2px 2px;
      border-radius: 8px;
      .v-progress-linear {
        .v-progress-linear__determinate {
          border-radius: 4px;
          margin: 0 10px 0 0;
        }
      }
    }
    .minting-video-space {
      position: absolute;
      top: 0;
      left: 0;
      width: 100vw;
      max-height: 100vh;
      margin: 0;
      padding: 0;
      z-index: 0;
      .minting-video-aspect-setter {
        position: relative;
        overflow: hidden;
        width: 100%;
        max-width: 1350px;
        max-height: 100vh;
        margin: 0 auto;
        &::before {
          padding-bottom: 86%;
          content: "";
          width: 1px;
          margin-left: -1px;
          float: left;
          height: 0;
        }
        &::after {
          content: "";
          display: table;
          clear: both;
        }
        .minting-video-container {
          width: 100%;
          height: 100%;
          position: relative;
          overflow: hidden;
          .minting-video-player {
            position: relative;
            width: 200%;
            height: 100%;
            margin: 0 -50%;
            opacity: 0.75;
          }
          .minting-diagonal-cut {
            background-color: $background;
            width: 100%;
            height: 80%;
            position: absolute;
            bottom: -48%;
            z-index: 1;
            transform: skewY(-7deg);
          }
        }
      }

    }
  }

</style>
